import type { ParentType } from '@risksmart-app/domain/src/types/consts';

export const workflows = {
  document: ['publish-document-version'],
  risk: [
    'update-risk-details',
    'open-acceptance',
    'delete-acceptance',
    'delete-risk',
  ],
  control: ['delete-control', 'update-control-details'],
  issue: [
    'delete-issue',
    'close-issue-assessment',
    'update-issue-assessment-target-close-date',
  ],
  action: [
    'close-action',
    'delete-action',
    'update-action-details',
    'update-action-target-close-date',
  ],
} as const satisfies Partial<Record<ParentType, readonly string[]>>;

export type WorkflowId = (typeof workflows)[keyof typeof workflows][number];

/**
 * Flat runtime tuple of every {@link WorkflowId}, derived from `workflows` so
 * it can never drift. Use this where a runtime list is needed — e.g. building a
 * `z.enum(workflowIds)` schema whose inferred type is `WorkflowId`.
 */
export const workflowIds = [
  ...workflows.document,
  ...workflows.risk,
  ...workflows.control,
  ...workflows.issue,
  ...workflows.action,
] as const;

export type ApprovalResourceType = keyof typeof workflows;

/**
 * Every {@link ParentType} that owns at least one approval workflow. This is the
 * set of resource types for which an approval configuration can exist, and the
 * domain over which the `${resourceType}_approval` Manager permission is
 * checked. Declared as an explicit tuple (rather than `Object.keys`) so the
 * element type stays `ApprovalResourceType` without a cast.
 */
export const approvalResourceTypes = [
  'document',
  'risk',
  'control',
  'issue',
  'action',
] as const satisfies readonly ApprovalResourceType[];

/**
 * Resolve the owning {@link ParentType} (resource type) for a given approval
 * workflow id. Returns `undefined` for an unknown workflow id. Used both to
 * group/filter the workflow selector and to gate per-row affordances on an
 * existing approval (parented approvals derive their resource type from the
 * parent node's ObjectType; global approvals derive it from the workflow id).
 */
export const getResourceTypeForWorkflow = (
  workflow: string
): ApprovalResourceType | undefined =>
  approvalResourceTypes.find((resourceType) => {
    const ids: readonly string[] = workflows[resourceType];

    return ids.includes(workflow);
  });
